import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// MUI Components
import { Button } from '@mui/material';

// Tiptap
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import UnderlineExt from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { CustomImage as ImageExt } from "./CustomImageExt";
import LinkExt from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";

// Lucide
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Image as LucideImageIcon,
  Link as LucideLinkIcon,
  Unlink,
  ChevronDown,
} from "lucide-react";

// Assets
import videoBlock from "../../assets/img/Course/video-block.svg";
import imageBlock from "../../assets/img/Course/image-block.svg";
import pdfBlock from "../../assets/img/Course/pdf-block.svg";
import textBlock from "../../assets/img/Course/text-block.svg";
import dragIcon from "../../assets/img/Course/drag-icon.svg";
import threeDot from "../../assets/img/Course/three-dots.svg";
import video from "../../assets/img/Course/video.svg";
import trash from "../../assets/img/Course/trash.svg";
import textIcon from "../../assets/img/Course/text-icon.svg";
import playCircle from "../../assets/img/Course/play-circle.svg";
import imageIcon from "../../assets/img/Course/image-icon.svg";  // ✅ lowercase
import cloudUplod from "../../assets/img/Course/cloud-arrow-up.svg";
import fileFomat from "../../assets/img/Course/file-format-icon.svg";
import pdfIcon from "../../assets/img/Course/pdf-icon.svg";
import fileNote from "../../assets/img/Course/file-format-icon-note.svg";
import pdffile from "../../assets/img/Course/pdf-file.svg";


const BLOCK_TYPES = [
  { type: 'text', label: 'Text Block', icon: textBlock },
  { type: 'video', label: 'Video Block', icon: videoBlock },
  { type: 'image', label: 'Image Block', icon: imageBlock },
  { type: 'pdf', label: 'PDF Block', icon: pdfBlock },
];

let blockIdCounter = 4;

const initialBlocks = [
  { id: 1, type: 'text', content: '' },
  { id: 2, type: 'video', url: '', preview: null },
  { id: 3, type: 'image', file: null, preview: null },
  { id: 4, type: 'pdf', file: null },
];

export const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  let match;

  match = url.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (match && match[1]) {
    return `https://www.youtube.com/embed/${match[1]}`;
  }

  match = url.match(/(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/);
  if (match && match[1]) {
    return `https://player.vimeo.com/video/${match[1]}`;
  }

  return null;
};

const CustomLink = LinkExt.extend({
  renderHTML({ HTMLAttributes }) {
    const attributes = { ...HTMLAttributes };
    if (attributes.href) {
      attributes.title = attributes.href;
      // Added a class to ensure external styling can target it if needed
      attributes.class = 'tiptap-link';
    }
    return ['a', attributes, 0];
  }
});

// ─── Text Block ────────────────────────────────────────────────────────────────
const TextBlock = ({ block, onChange, onDelete, provided }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          HTMLAttributes: {
            style: 'color: #262626;',
          },
        },
      }),
      UnderlineExt,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      ImageExt,
      CustomLink.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: "Start writing your content..." }),
    ],
    content: block.content || "",
    onUpdate: ({ editor }) => {
      onChange({ ...block, content: editor.getHTML() });
    }
  });

  if (!editor) return null;

  const getCurrentFormat = () => {
    if (editor.isActive("heading", { level: 1 })) return "Heading 1";
    if (editor.isActive("heading", { level: 2 })) return "Heading 2";
    if (editor.isActive("heading", { level: 3 })) return "Heading 3";
    return "Paragraph";
  };

  const ToolbarButton = ({ onMouseDown, isActive = false, children, disabled = false }) => (
    <button
      onMouseDown={onMouseDown}
      disabled={disabled}
      className={`toolbar-btn ${isActive ? "is-active" : ""}`}
    >
      {children}
    </button>
  );

  const Divider = () => <div className="toolbar-divider" />;

  return (
    <div className="lesson-block" id={`block-${block.id}`} ref={provided?.innerRef} {...provided?.draggableProps}>
      <div className="lesson-block-header">
        <div className="lesson-block-header-left">
          <img src={threeDot} alt="threeDot" className="threedots" {...provided?.dragHandleProps} />
          <img src={textIcon} alt="textIcon" />
          <span className="block-type-label">Text</span>
        </div>
        <img src={trash} alt="trash" onClick={onDelete} className='trash' />
      </div>

      <div className="lesson-block-body text-block">
        <div className="rich-text-toolbar">
          <div className="format-dropdown-container" ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              className="dropdown-btn"
              onClick={(e) => { e.preventDefault(); setDropdownOpen(!dropdownOpen); }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <span>{getCurrentFormat()}</span>
              <ChevronDown size={14} className="dropdown-icon" />
            </button>
            {dropdownOpen && (
              <div className="format-dropdown-menu" style={{
                position: 'absolute', top: '114%', left: -10, zIndex: 10,
                background: '#fff', border: '1px solid #e0e0e0',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)', minWidth: '121px',
                display: 'flex', flexDirection: 'column',
              }}>
                <div
                  className={`format-dropdown-item ${editor.isActive('paragraph') ? 'is-active' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    editor.chain().focus().setParagraph().run();
                    setDropdownOpen(false);
                  }}
                  style={{ padding: '8px 16px', cursor: 'pointer', fontSize: '14px', background: editor.isActive('paragraph') ? '#f5f5f5' : 'transparent', color: '#333' }}
                >
                  Paragraph
                </div>
                <div
                  className={`format-dropdown-item ${editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleHeading({ level: 1 }).run();
                    setDropdownOpen(false);
                  }}
                  style={{ padding: '8px 16px', cursor: 'pointer', fontSize: '14px', background: editor.isActive('heading', { level: 1 }) ? '#f5f5f5' : 'transparent', color: '#333' }}
                >
                  Heading 1
                </div>
                <div
                  className={`format-dropdown-item ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleHeading({ level: 2 }).run();
                    setDropdownOpen(false);
                  }}
                  style={{ padding: '8px 16px', cursor: 'pointer', fontSize: '14px', background: editor.isActive('heading', { level: 2 }) ? '#f5f5f5' : 'transparent', color: '#333' }}
                >
                  Heading 2
                </div>
                <div
                  className={`format-dropdown-item ${editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleHeading({ level: 3 }).run();
                    setDropdownOpen(false);
                  }}
                  style={{ padding: '8px 16px', cursor: 'pointer', fontSize: '14px', background: editor.isActive('heading', { level: 3 }) ? '#f5f5f5' : 'transparent', color: '#333' }}
                >
                  Heading 3
                </div>
              </div>
            )}
          </div>

          <Divider />

          <ToolbarButton
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
            isActive={editor.isActive("bold")}
          >
            <Bold size={16} />
          </ToolbarButton>

          <ToolbarButton
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
            isActive={editor.isActive("italic")}
          >
            <Italic size={16} />
          </ToolbarButton>

          <ToolbarButton
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleUnderline().run(); }}
            isActive={editor.isActive("underline")}
          >
            <UnderlineIcon size={16} />
          </ToolbarButton>

          <Divider />

          <ToolbarButton
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign("left").run(); }}
            isActive={editor.isActive({ textAlign: "left" })}
          >
            <AlignLeft size={16} />
          </ToolbarButton>

          <ToolbarButton
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign("center").run(); }}
            isActive={editor.isActive({ textAlign: "center" })}
          >
            <AlignCenter size={16} />
          </ToolbarButton>

          <ToolbarButton
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign("right").run(); }}
            isActive={editor.isActive({ textAlign: "right" })}
          >
            <AlignRight size={16} />
          </ToolbarButton>

          <ToolbarButton
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign("justify").run(); }}
            isActive={editor.isActive({ textAlign: "justify" })}
          >
            <AlignJustify size={16} />
          </ToolbarButton>

          <Divider />

          <ToolbarButton
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}
            isActive={editor.isActive("bulletList")}
          >
            <List size={16} />
          </ToolbarButton>

          <ToolbarButton
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}
            isActive={editor.isActive("orderedList")}
          >
            <ListOrdered size={16} />
          </ToolbarButton>

          <Divider />

          <ToolbarButton
            onMouseDown={(e) => {
              e.preventDefault();
              const url = window.prompt("URL");
              if (url) editor.chain().focus().setImage({ src: url }).run();
            }}
          >
            <LucideImageIcon size={16} />
          </ToolbarButton>

          {editor.isActive("link") ? (
            <ToolbarButton
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().unsetLink().run();
              }}
              isActive={true}
            >
              <Unlink size={16} />
            </ToolbarButton>
          ) : (
            <ToolbarButton
              onMouseDown={(e) => {
                e.preventDefault();
                const url = window.prompt("URL");
                if (url) editor.chain().focus().setLink({ href: url }).run();
              }}
            >
              <LucideLinkIcon size={16} />
            </ToolbarButton>
          )}
        </div>

        <EditorContent editor={editor} className="tiptap-editor-content" />
      </div>
    </div>
  );
};

// ─── Video Block ───────────────────────────────────────────────────────────────
const VideoBlock = ({ block, onChange, onDelete, provided, setToast }) => {
  const handleUrlValidation = () => {
    const url = block.url;
    if (url) {
      const embedUrl = getVideoEmbedUrl(url);
      if (!embedUrl) {
        setToast({ open: true, message: "Please enter a valid video URL.", severity: 'error' });
        onChange({ ...block, url: '' });
      } else if (url !== embedUrl) {
        onChange({ ...block, url: embedUrl });
      }
    }
  };

  return (
    <div className="lesson-block" id={`block-${block.id}`} ref={provided?.innerRef} {...provided?.draggableProps}>
      <div className="lesson-block-header">
        <div className="lesson-block-header-left">
          <img src={threeDot} alt="threeDot" className='threedots' {...provided?.dragHandleProps} />
          <img src={video} alt='video' />
          <span className="block-type-label">Video</span>
        </div>
        <img src={trash} alt='trash' onClick={onDelete} className='trash' />
      </div>
      <div className="lesson-block-body">
        <div className="video-url-row">
          <span className="video-url-label">Video URL</span>
        </div>
        <div className='video-url-wrapper'>
          <input
            type="text"
            className="video-url-input"
            placeholder="Paste Youtube or Vimeo URL here"
            value={block.url}
            onChange={(e) => onChange({ ...block, url: e.target.value })}
            onBlur={handleUrlValidation}
          />
          <Button className="embed" onClick={handleUrlValidation}>Embed</Button>
        </div>
        <div className="video-preview-label">Preview</div>
        <div className="video-preview-box">
          {getVideoEmbedUrl(block.url) ? (
            <iframe
              src={getVideoEmbedUrl(block.url)}
              title="video preview"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
              allowFullScreen
              style={{ width: '100%', height: '100%', borderRadius: '4px' }}
            />
          ) : (
            <div className="video-preview-placeholder">
              <img src={playCircle} alt="playCircle" />
              <span>Video preview will appear here</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
};

// ─── Image Block ───────────────────────────────────────────────────────────────
const ImageBlock = ({ block, onChange, onDelete, provided, setToast }) => {
  const inputRef = React.useRef(null);
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setToast({ open: true, message: "Please upload only image files (JPG, PNG, GIF, WebP)", severity: 'error' });
        e.target.value = '';
        return;
      }
      onChange({ ...block, file, preview: URL.createObjectURL(file) });
    }
  };

  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length > 0) {
      const file = e.dataTransfer.files[0];
      if (!file.type.startsWith('image/')) {
        setToast({ open: true, message: "Please upload only image files (JPG, PNG, GIF, WebP)", severity: 'error' });
        return;
      }
      onChange({ ...block, file, preview: URL.createObjectURL(file) });
    }
  };

  const displayPreview = block.preview || block.content || null;

  return (
    <div className="lesson-block" id={`block-${block.id}`} ref={provided?.innerRef} {...provided?.draggableProps}>
      <div className="lesson-block-header">
        <div className="lesson-block-header-left">
          <img src={threeDot} alt="threeDot" className='threedots' {...provided?.dragHandleProps} />
          <img src={imageIcon} alt='imageIcon' />
          <span className="block-type-label">Image</span>
        </div>
        <img src={trash} alt='trash' onClick={onDelete} className='trash' />
      </div>
      <div className="lesson-block-body">
        <div className="image-preview-label">Preview</div>
        <div
          className="image-upload-box"
          onClick={() => inputRef.current.click()}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {displayPreview ? (
            <img src={displayPreview} alt="preview" className="image-preview-img" />
          ) : (
            <div className="image-upload-placeholder">
              <div className='upload-content'>
                <img src={cloudUplod} alt="cloudUplod" />
                Upload an image or drag & drop
              </div>
              <div className='recommended-format'>Recommended 16:9 or 4:3</div>
            </div>
          )}
        </div>
        <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.gif,.webp" style={{ display: 'none' }} onChange={handleFile} />
        <div className="image-format-note">
          <img src={fileFomat} alt="fileFomat" />
          Maximum file size: 5 MB. Supported formats: JPG, PNG, GIF, WebP.
        </div>
      </div>
    </div>
  );
};

// ─── PDF Block ─────────────────────────────────────────────────────────────────
const PDFBlock = ({ block, onChange, onDelete, provided, setToast }) => {
  const inputRef = React.useRef(null);
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setToast({ open: true, message: "Please upload only PDF files", severity: 'error' });
        e.target.value = '';
        return;
      }
      onChange({ ...block, file, original_filename: file.name });
    }
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e) => {
    e.preventDefault();
    if (!block.file && e.dataTransfer.files?.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type !== 'application/pdf') {
        setToast({ open: true, message: "Please upload only PDF files", severity: 'error' });
        return;
      }
      onChange({ ...block, file, original_filename: file.name });
    }
  };

  const hasExistingPdf = !block.file && block.content;
  const hasFile = !!block.file;

  return (
    <div className="lesson-block" id={`block-${block.id}`} ref={provided?.innerRef} {...provided?.draggableProps}>
      <div className="lesson-block-header">
        <div className="lesson-block-header-left">
          <img src={threeDot} alt="threeDot" className='threedots' {...provided?.dragHandleProps} />
          <img src={pdfIcon} alt='pdfIcon' />
          <span className="block-type-label">PDF</span>
        </div>
        <img src={trash} alt='trash' onClick={onDelete} className='trash' />
      </div>
      <div className="lesson-block-body">
        <div
          className={`${hasFile || hasExistingPdf ? "uploaded-pdf-container" : "pdf-upload-box"}`}
          onClick={() => { if (!hasFile && !hasExistingPdf) inputRef.current.click(); }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {hasFile ? (
            <div className='pdf-container'>
              <div className='pdf-details'>
                <img src={pdffile} alt="pdffile" />
                <div className='pdf-filename'>{block.original_filename || block.file.name}</div>
              </div>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <a
                  href={URL.createObjectURL(block.file)}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={block.original_filename || block.file.name}
                  onClick={(e) => e.stopPropagation()}
                  className='link-text'
                >
                  Download
                </a>
                <div className='remove-pdf' onClick={(e) => {
                  e.stopPropagation();
                  onChange({ ...block, file: null, original_filename: null });
                  if (inputRef.current) inputRef.current.value = "";
                }}>Remove</div>
              </div>
            </div>
          ) : hasExistingPdf ? (
            <div className='pdf-container'>
              <div className='pdf-details'>
                <img src={pdffile} alt="pdffile" />
                <div className='pdf-filename'>{block.original_filename || block.content.split('/').pop()}</div>
              </div>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <a
                  href={block.content}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={block.original_filename || block.content.split('/').pop()}
                  onClick={(e) => e.stopPropagation()}
                  className='link-text'
                >
                  Download
                </a>
                <div className='remove-pdf' onClick={(e) => {
                  e.stopPropagation();
                  onChange({ ...block, content: null, original_filename: null });
                }}>Remove</div>
              </div>
            </div>
          ) : (
            <div className="upload-wrapper">
              <div className='upload-content'>
                <img src={cloudUplod} alt="cloudUplod" />
                Upload a PDF file
              </div>
              <div className='drag-and-drop-text'>Drag and drop your file here, or browse</div>
            </div>
          )}
        </div>
        <input ref={inputRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleFile} />
        <div className="image-format-note">
          <img src={fileNote} alt="fileNote" />
          Uploaded PDFs will be downloadable for your learners. Max 20 MB.
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const LessonContent = ({ blocks, setBlocks, onSave, hasLengthError, isloading, isEditMode, setToast }) => {
  const scrollSpeed = React.useRef(0);
  const isDragging = React.useRef(false);

  React.useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      const threshold = 100;
      if (e.clientY < threshold) {
        scrollSpeed.current = -15;
      } else if (window.innerHeight - e.clientY < threshold) {
        scrollSpeed.current = 15;
      } else {
        scrollSpeed.current = 0;
      }
    };

    const doScroll = () => {
      if (isDragging.current && scrollSpeed.current !== 0) {
        const container = document.querySelector('section');
        if (container) {
          container.scrollBy(0, scrollSpeed.current);
        } else {
          window.scrollBy(0, scrollSpeed.current);
        }
      }
      requestAnimationFrame(doScroll);
    };

    window.addEventListener('mousemove', handleMouseMove);
    const frameId = requestAnimationFrame(doScroll);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(frameId);
    };
  }, []);

  const onDragStart = () => {
    isDragging.current = true;
  };

  const onDragEnd = (result) => {
    isDragging.current = false;
    scrollSpeed.current = 0;
    if (!result.destination) return;
    const items = Array.from(blocks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setBlocks(items);
  };

  const addBlock = (type) => {
    const nextId = blocks.length > 0 ? Math.max(...blocks.map(b => b.id)) + 1 : 1;
    const base = { id: nextId, type };
    if (type === 'text') setBlocks(prev => [...prev, { ...base, content: '' }]);
    else if (type === 'video') setBlocks(prev => [...prev, { ...base, url: '', preview: null }]);
    else if (type === 'image') setBlocks(prev => [...prev, { ...base, file: null, preview: null }]);
    else if (type === 'pdf') setBlocks(prev => [...prev, { ...base, file: null }]);

    setTimeout(() => {
      const el = document.getElementById(`block-${nextId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const deleteBlock = (id) => setBlocks(prev => prev.filter(b => b.id !== id));
  const updateBlock = (id, updated) => setBlocks(prev => prev.map(b => b.id === id ? updated : b));

  const renderBlock = (block, index) => {
    const commonProps = {
      block,
      onChange: (u) => updateBlock(block.id, u),
      onDelete: () => deleteBlock(block.id),
      setToast,
    };

    return (
      <Draggable key={block.id.toString()} draggableId={block.id.toString()} index={index}>
        {(provided) => {
          switch (block.type) {
            case 'text': return <TextBlock {...commonProps} provided={provided} />;
            case 'video': return <VideoBlock {...commonProps} provided={provided} />;
            case 'image': return <ImageBlock {...commonProps} provided={provided} />;
            case 'pdf': return <PDFBlock {...commonProps} provided={provided} />;
            default: return null;
          }
        }}
      </Draggable>
    );
  };

  return (
    <div className="lesson-content-container">
      <div className="lesson-content-header">
        <div className="lesson-content-title">Lesson Content</div>
        <div className="lesson-content-subtitle">
          Add and arrange blocks to build your lesson. You can include text, videos, and PDFs.
        </div>
      </div>

      <div className="block-type-buttons">
        {BLOCK_TYPES.map(({ type, label, icon }) => (
          <button
            key={type}
            className="block-type-btn"
            onClick={() => addBlock(type)}
            draggable="true"
            onDragStart={(e) => {
              e.dataTransfer.setData('application/react-block-type', type);
            }}
          >
            <img src={icon} alt="icon" />
            <span>{label}</span>
          </button>
        ))}
      </div>

      <div
        className="drag-hint"
        style={{
          opacity: blocks.length === 0 ? 1 : 0,
          height: blocks.length === 0 ? '85px' : '0px',
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out',
          borderWidth: blocks.length === 0 ? '2px' : '0px',
          margin: blocks.length === 0 ? undefined : '0px',
          padding: blocks.length === 0 ? undefined : '0px'
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const type = e.dataTransfer.getData('application/react-block-type');
          if (type) {
            addBlock(type);
          }
        }}
      >
        <img src={dragIcon} alt="dragIcon" />
        <div className='drag-text-container'>
          <p className='drag-title'>Drag blocks here to get started</p>
          <p>or select a block above</p>
        </div>
      </div>

      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <Droppable droppableId="lesson-blocks-list">
          {(provided) => (
            <div
              className="blocks-list"
              {...provided.droppableProps}
              ref={provided.innerRef}
              onDragOver={(e) => {
                if (e.dataTransfer.types.includes('application/react-block-type')) {
                  e.preventDefault();
                }
              }}
              onDrop={(e) => {
                const type = e.dataTransfer.getData('application/react-block-type');
                if (type) {
                  e.preventDefault();
                  addBlock(type);
                }
              }}
            >
              {blocks.map((block, index) => renderBlock(block, index))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className="lesson-content-footer">
        <Button className="create-lesson-btn" onClick={onSave} disabled={isloading}>
          {isloading ? "Saving..." : isEditMode ? "Save" : "Create Lesson"}
        </Button>
      </div>
    </div>
  );
};

export default LessonContent;